﻿import { GeoJSON } from 'ol/format';
import { Feature } from 'ol';
import GeometryCollection from 'ol/geom/GeometryCollection.js';
import LineString from 'ol/geom/LineString.js';
import MultiLineString from 'ol/geom/MultiLineString.js';
import MultiPoint from 'ol/geom/MultiPoint.js';
import MultiPolygon from 'ol/geom/MultiPolygon.js';
import Point from 'ol/geom/Point.js';
import Polygon from 'ol/geom/Polygon.js';
import { transformGeometryWithOptions } from 'ol/format/Feature.js';

class XGeoJSON extends GeoJSON {
    constructor(options) {
        super(options);
        this.layerId = options.layerId;
        this.layerSettings = options.layerSettings;
        this.selectionHandler = options.selectionHandler;
        this.generalActionHandler = options.generalActionHandler;
    }

    readFeatureFromObject(object, options) {
        /**
         * @type {GeoJSONFeature}
         */
        let geoJSONFeature = null;
        if (object['type'] === 'Feature') {
            geoJSONFeature = /** @type {GeoJSONFeature} */ (object);
        } else {
            geoJSONFeature = {
                'type': 'Feature',
                'geometry': /** @type {GeoJSONGeometry} */ (object),
                'properties': null,
            };
        }

        const geometry = readGeometry(geoJSONFeature['geometry'], options);
            //.simplify(100);
        const feature = new Feature();
        if (this.geometryName_) {
            feature.setGeometryName(this.geometryName_);
        } else if (
            this.extractGeometryName_ &&
            'geometry_name' in geoJSONFeature !== undefined
        ) {
            feature.setGeometryName(geoJSONFeature['geometry_name']);
        }
        feature.setGeometry(geometry);

        if ('id' in geoJSONFeature) {
            feature.setId(geoJSONFeature['id']);
        }

        if (geoJSONFeature['properties']) {
            let regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+\-]\d{2}:\d{2})$/;
            let props = {};
            let keys = Object.keys(geoJSONFeature['properties']);

            for (let i = 0; i < keys.length; i++) {
                let key = keys[i];
                let prop = geoJSONFeature['properties'][key];
                if (prop != null && typeof prop === 'string' && regex.test(prop)) {
                    props[key + '_tst'] = new Date(prop).getTime();
                }
                props[key] = prop;
            }

            feature.setProperties(props, true);
        }

        if (this.layerId != null) {
            feature.layerId = this.layerId;
        }

        if (this.layerSettings.Entity != null && this.selectionHandler != null && this.selectionHandler.selectionHolder != null
            && this.selectionHandler.selectionHolder.layers[this.layerId] != null && this.selectionHandler.selectionHolder.layers[this.layerId].features != null
            && this.selectionHandler.selectionHolder.layers[this.layerId].features[feature.get(this.layerSettings.Entity.PkFieldName)] != null) {
            feature.set('xstate', 2);
            this.selectionHandler.selectionHolder.layers[this.layerId].features[feature.get(this.layerSettings.Entity.PkFieldName)] = feature;
        } else if (this.layerSettings.Entity != null && this.generalActionHandler != null && this.generalActionHandler.generalActionControls != null
            && this.generalActionHandler.generalActionControls.editControl != null && this.generalActionHandler.generalActionControls.editControl.featureInteractionHandler != null
            && this.generalActionHandler.generalActionControls.editControl.featureInteractionHandler.layerId == this.layerId
            && this.generalActionHandler.generalActionControls.editControl.featureInteractionHandler.features.find(x => x.get(this.layerSettings.Entity.PkFieldName) == feature.get(this.layerSettings.Entity.PkFieldName)) != null) {
            feature.set('xstate', 4);
        } else {
            feature.set('xstate', 0);
        }

        return feature;
    }
}

function readGeometry(object, options) {
    if (!object) {
        return null;
    }

    /**
     * @type {import("../geom/Geometry.js").default}
     */
    let geometry;
    switch (object['type']) {
        case 'Point': {
            geometry = readPointGeometry(/** @type {GeoJSONPoint} */(object));
            break;
        }
        case 'LineString': {
            geometry = readLineStringGeometry(
        /** @type {GeoJSONLineString} */(object)
            );
            break;
        }
        case 'Polygon': {
            geometry = readPolygonGeometry(/** @type {GeoJSONPolygon} */(object));
            break;
        }
        case 'MultiPoint': {
            geometry = readMultiPointGeometry(
        /** @type {GeoJSONMultiPoint} */(object)
            );
            break;
        }
        case 'MultiLineString': {
            geometry = readMultiLineStringGeometry(
        /** @type {GeoJSONMultiLineString} */(object)
            );
            break;
        }
        case 'MultiPolygon': {
            geometry = readMultiPolygonGeometry(
        /** @type {GeoJSONMultiPolygon} */(object)
            );
            break;
        }
        case 'GeometryCollection': {
            geometry = readGeometryCollectionGeometry(
        /** @type {GeoJSONGeometryCollection} */(object)
            );
            break;
        }
        default: {
            throw new Error('Unsupported GeoJSON type: ' + object['type']);
        }
    }
    return transformGeometryWithOptions(geometry, false, options);
}

/**
 * @param {GeoJSONGeometryCollection} object Object.
 * @param {import("./Feature.js").ReadOptions} [options] Read options.
 * @return {GeometryCollection} Geometry collection.
 */
function readGeometryCollectionGeometry(object, options) {
    const geometries = object['geometries'].map(
        /**
         * @param {GeoJSONGeometry} geometry Geometry.
         * @return {import("../geom/Geometry.js").default} geometry Geometry.
         */
        function (geometry) {
            return readGeometry(geometry, options);
        }
    );
    return new GeometryCollection(geometries);
}

/**
 * @param {GeoJSONPoint} object Object.
 * @return {Point} Point.
 */
function readPointGeometry(object) {
    return new Point(object['coordinates']);
}

/**
 * @param {GeoJSONLineString} object Object.
 * @return {LineString} LineString.
 */
function readLineStringGeometry(object) {
    return new LineString(object['coordinates']);
}

/**
 * @param {GeoJSONMultiLineString} object Object.
 * @return {MultiLineString} MultiLineString.
 */
function readMultiLineStringGeometry(object) {
    return new MultiLineString(object['coordinates']);
}

/**
 * @param {GeoJSONMultiPoint} object Object.
 * @return {MultiPoint} MultiPoint.
 */
function readMultiPointGeometry(object) {
    return new MultiPoint(object['coordinates']);
}

/**
 * @param {GeoJSONMultiPolygon} object Object.
 * @return {MultiPolygon} MultiPolygon.
 */
function readMultiPolygonGeometry(object) {
    return new MultiPolygon(object['coordinates']);
}

/**
 * @param {GeoJSONPolygon} object Object.
 * @return {Polygon} Polygon.
 */
function readPolygonGeometry(object) {
    return new Polygon(object['coordinates']);
}

/**
 * @param {import("../geom/Geometry.js").default} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeGeometry(geometry, options) {
    geometry = transformGeometryWithOptions(geometry, true, options);
    const type = geometry.getType();

    /** @type {GeoJSONGeometry} */
    let geoJSON;
    switch (type) {
        case 'Point': {
            geoJSON = writePointGeometry(/** @type {Point} */(geometry), options);
            break;
        }
        case 'LineString': {
            geoJSON = writeLineStringGeometry(
        /** @type {LineString} */(geometry),
                options
            );
            break;
        }
        case 'Polygon': {
            geoJSON = writePolygonGeometry(
        /** @type {Polygon} */(geometry),
                options
            );
            break;
        }
        case 'MultiPoint': {
            geoJSON = writeMultiPointGeometry(
        /** @type {MultiPoint} */(geometry),
                options
            );
            break;
        }
        case 'MultiLineString': {
            geoJSON = writeMultiLineStringGeometry(
        /** @type {MultiLineString} */(geometry),
                options
            );
            break;
        }
        case 'MultiPolygon': {
            geoJSON = writeMultiPolygonGeometry(
        /** @type {MultiPolygon} */(geometry),
                options
            );
            break;
        }
        case 'GeometryCollection': {
            geoJSON = writeGeometryCollectionGeometry(
        /** @type {GeometryCollection} */(geometry),
                options
            );
            break;
        }
        case 'Circle': {
            geoJSON = {
                type: 'GeometryCollection',
                geometries: [],
            };
            break;
        }
        default: {
            throw new Error('Unsupported geometry type: ' + type);
        }
    }
    return geoJSON;
}

/**
 * @param {GeometryCollection} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometryCollection} GeoJSON geometry collection.
 */
function writeGeometryCollectionGeometry(geometry, options) {
    options = Object.assign({}, options);
    delete options.featureProjection;
    const geometries = geometry.getGeometriesArray().map(function (geometry) {
        return writeGeometry(geometry, options);
    });
    return {
        type: 'GeometryCollection',
        geometries: geometries,
    };
}

/**
 * @param {LineString} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeLineStringGeometry(geometry, options) {
    return {
        type: 'LineString',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {MultiLineString} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiLineStringGeometry(geometry, options) {
    return {
        type: 'MultiLineString',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {MultiPoint} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiPointGeometry(geometry, options) {
    return {
        type: 'MultiPoint',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {MultiPolygon} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writeMultiPolygonGeometry(geometry, options) {
    let right;
    if (options) {
        right = options.rightHanded;
    }
    return {
        type: 'MultiPolygon',
        coordinates: geometry.getCoordinates(right),
    };
}

/**
 * @param {Point} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writePointGeometry(geometry, options) {
    return {
        type: 'Point',
        coordinates: geometry.getCoordinates(),
    };
}

/**
 * @param {Polygon} geometry Geometry.
 * @param {import("./Feature.js").WriteOptions} [options] Write options.
 * @return {GeoJSONGeometry} GeoJSON geometry.
 */
function writePolygonGeometry(geometry, options) {
    let right;
    if (options) {
        right = options.rightHanded;
    }
    return {
        type: 'Polygon',
        coordinates: geometry.getCoordinates(right),
    };
}

export default XGeoJSON;